import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock, Zap, ShieldCheck, CheckCircle2 } from "lucide-react";

export default function ApplyCTA() {
  return (
    <section
      id="apply"
      data-testid="apply-cta-section"
      className="relative py-14 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#0052FF]/15 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#00FFA3]/10 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7"
          >
            <p className="text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-[#0052FF] mb-4">
              / 08 — Apply Now
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tighter uppercase leading-[0.92] mb-6">
              Move <span className="text-gradient-neon">forward.</span><br />Right now.
            </h2>
            <p className="text-zinc-400 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl">
              Under 2 minutes. Soft credit check. Multi-offer return same day. The shortest path between capital and your business.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                to="/application"
                data-testid="apply-cta-start"
                className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#0052FF] to-[#00FFA3] text-black font-bold animate-pulse-glow hover:scale-[1.02] transition-transform"
              >
                Start Application
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:+19086767979"
                data-testid="apply-cta-call"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full glass text-white font-semibold hover:border-[#0052FF]/50 transition-all"
              >
                Speak To An Advisor
              </a>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {[
                { icon: Zap, label: "Decisions in 2–4 hours" },
                { icon: Lock, label: "256-bit bank-grade encryption" },
                { icon: ShieldCheck, label: "Soft pull — no FICO impact" },
                { icon: CheckCircle2, label: "Dedicated senior advisor" },
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-zinc-300">
                  <item.icon className="w-5 h-5 text-[#00FFA3] shrink-0" />
                  <span className="text-sm md:text-base">{item.label}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Visual card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <div className="glass-strong rounded-3xl p-8 border border-white/10 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#00FFA3]/10 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0052FF] to-[#00FFA3] grid place-items-center">
                    <Zap className="w-6 h-6 text-black" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Application</p>
                    <p className="font-display font-bold text-lg">8 steps · ~2 min</p>
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  {[
                    "Funding amount",
                    "Monthly revenue",
                    "Time in business",
                    "Business profile",
                    "Owner info & docs",
                  ].map((s, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <div className="w-6 h-6 rounded-full bg-[#00FFA3]/15 border border-[#00FFA3]/30 grid place-items-center text-[10px] font-mono text-[#00FFA3]">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <span className="text-zinc-300">{s}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#00FFA3]/20">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Expected outcome</p>
                  <p className="font-display text-2xl font-extrabold text-gradient-neon">Pre-approval in 60 sec</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
